import { useCallback, useEffect, useState } from 'react';
import { Enum_LiveStream_Provider } from '@core/api';
import { useContextTranslation } from '@core/i18n';
import { isRestreaming } from '@core/livestream/helpers';
import { isIVS, isVideo } from '@core/video/videoElementWrapper/helpers';
import { useSubtitles } from '@embed/components/VideoPlayer/Player3/features/TextCues/hooks/useSubtitles';
import { useVideoEl } from '@embed/components/VideoPlayer/Player3/hooks/useVideoEl';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { VTT_TEXT_CUES } from '@player/testIds';
import { CueWrapper, TextCueWrapper } from '../TextCues.styles';
import { formatLiveStreamSubtitles, formatVideoSubtitles } from './helpers';
const AUTOGENERATED_MESSAGE_DURATION = 2000;
/**
 * TextCues component to display subtitles
 */
export const VTTTextCues = ({ initialCaption = '', setShowAutogeneratedMessage, showAutogeneratedMessage, top, }) => {
    const videoEl = useVideoEl();
    const { t } = useContextTranslation();
    const liveStreamState = useStore((state) => state.liveStreamState);
    const { currentSubtitles, subsAreEnabled } = useSubtitles();
    // Checking if livestream is a restream and if it is playing out from IVS/Agora
    const isRestream = isRestreaming(videoEl.video, liveStreamState) &&
        videoEl.video?.live_stream_provider !== Enum_LiveStream_Provider.CDN;
    useEffect(() => {
        if (currentSubtitles && showAutogeneratedMessage === null) {
            const url = new URL(currentSubtitles.src);
            if (!url.pathname.startsWith('/medias')) {
                setShowAutogeneratedMessage(true);
                // [CS-5503]: show auto-generated message for 2 seconds
                setTimeout(() => {
                    setShowAutogeneratedMessage(false);
                }, AUTOGENERATED_MESSAGE_DURATION);
            }
        }
    }, [currentSubtitles, setShowAutogeneratedMessage, showAutogeneratedMessage]);
    const [curCaption, setCurCaption] = useState(initialCaption);
    const [tracks, setTracks] = useState(null);
    const [textCues, setTextCues] = useState([]);
    const ccListener = useCallback((e) => {
        const track = e.target;
        if (isRestream && track.cues) {
            const cues = Array.from(track.cues);
            setTextCues(cues);
        }
        else {
            const activeCue = track.activeCues?.[0];
            setCurCaption(activeCue ? activeCue.text : '');
        }
    }, [isRestream]);
    useEffect(() => {
        if (!isIVS(videoEl)) {
            return;
        }
        videoEl.registerTextCueCallback((streamTimeMs) => {
            if (isRestream) {
                const filteredCues = textCues.filter((cue) => cue.startTime < streamTimeMs / 1000 &&
                    streamTimeMs / 1000 < cue.endTime);
                const activeCue = filteredCues[0];
                setCurCaption(activeCue?.text || '');
            }
        });
        return () => videoEl.unregisterTextCueCallback();
    }, [isRestream, textCues, videoEl]);
    useEffect(() => {
        if (videoEl.video?.live_stream_subtitles?.length && isIVS(videoEl)) {
            setTracks(videoEl.setSubtitles(formatLiveStreamSubtitles(videoEl.video?.live_stream_subtitles)));
        }
        else if (videoEl.video?.video_subtitles?.length && isVideo(videoEl)) {
            if (currentSubtitles) {
                setTracks(videoEl.setSubtitles([currentSubtitles]));
            }
            else {
                setTracks(videoEl.setSubtitles(formatVideoSubtitles(videoEl.video?.video_subtitles)));
            }
        }
    }, [videoEl, currentSubtitles]);
    useEffect(() => {
        tracks?.forEach((track) => {
            track.track?.addEventListener('cuechange', ccListener);
        });
        return () => {
            tracks?.forEach((track) => {
                track.track?.removeEventListener('cuechange', ccListener);
            });
        };
    }, [ccListener, tracks]);
    return subsAreEnabled && (curCaption !== '' || showAutogeneratedMessage) ? (<CueWrapper data-testid={VTT_TEXT_CUES} top={top}>
      <TextCueWrapper>
        {showAutogeneratedMessage
            ? t('player:(Captions are auto-generated)')
            : curCaption}
      </TextCueWrapper>
    </CueWrapper>) : null;
};
